import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EnumWidgetEventSettingsDisplayType,
  EnumWidgetType,
  EventStatus,
  useEventManyAppLazyQuery,
} from "gql/generated";
import { ButtonProps } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { addMonths, format } from "date-fns";
import { EVENT_DATE_FORMAT } from "shared/utils";
import { useEventTags } from "shared/components/organisms/EventCreationAndEditing/EventCreateEditContext/useEventTags";
import { ComboboxGroupData } from "apps/events/types/ComboboxData";

type UseStartNewEventWidgetProps = {
  onClose: () => void;
  onNextClick: (
    widgetType: EnumWidgetType,
    name: string,
    listId?: string,
    eventSettings?: {
      displayType: EnumWidgetEventSettingsDisplayType;
      lookAheadMonths: number;
      eventTags: string[];
    },
  ) => void;
  setOnCloseClick: Dispatch<SetStateAction<() => void>>;
  setCancelButtonProps: Dispatch<SetStateAction<ButtonProps>>;
  setNextButtonProps: Dispatch<SetStateAction<ButtonProps>>;
};

export const useStartNewEventWidget = ({
  onClose,
  onNextClick,
  setOnCloseClick,
  setCancelButtonProps,
  setNextButtonProps,
}: UseStartNewEventWidgetProps) => {
  const { t } = useTranslation("widgets", {
    keyPrefix: "CREATE_NEW.EVENT_WIDGET",
  });
  const { organizationId } = useParams();

  /**
   * Default widget details
   */
  const defaultWidgetDetails = useMemo(
    () => ({
      type: EnumWidgetType.Event,
      name: "",
      eventSettings: {
        displayType: EnumWidgetEventSettingsDisplayType.Grid,
        eventTags: [] as string[],
        lookAheadMonths: 0,
      },
    }),
    [],
  );
  const [widgetDetails, setWidgetDetails] = useState(defaultWidgetDetails);

  /**
   * Look ahead months options
   * - Always from 1 to 12 months
   */
  const lookAheadMonthsOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: (i + 1).toString(),
      label: `${i + 1} ${t(`LOOK_AHEAD.MONTH`, { count: i + 1 })}`,
    }));
  }, [t]);

  /**
   * Load the event tags (all Global Event tags) + all organization tags
   * - The globalEventTags is an array of ComboboxGroupData
   * - The organizationEventTags is an array of ComboboxData
   * - We need to combine these two arrays into a single array of ComboboxGroupData
   */
  const {
    globalEventTags,
    organizationEventTags,
    loading: loadingEventTags,
    error: errorEventTags,
  } = useEventTags({
    isOrganization: true,
    organizationId: organizationId || "",
  });
  const [allEventTags, setAllEventTags] = useState<ComboboxGroupData[]>([]);
  useEffect(() => {
    setAllEventTags([
      ...globalEventTags,
      ...[{ group: "ORGANIZATION_TAGS", tags: organizationEventTags }],
    ]);
  }, [globalEventTags, organizationEventTags]);

  /**
   * Filter the tags based on the search input
   * - Start with all tags
   */
  const [filteredTags, setFilteredTags] = useState<ComboboxGroupData[]>([]);
  useEffect(() => {
    setFilteredTags([...allEventTags]);
  }, [allEventTags]);

  /**
   * Filter the tags based on the search input
   */
  const handleFilterTags = useCallback(
    (search: string) => {
      const inputValue = (search.toLowerCase().split(",").pop() ?? "").trim();
      const filteredTags = allEventTags
        .map((group) => ({
          ...group,
          tags: group.tags.filter((tag) =>
            tag.label.toLowerCase().includes(inputValue),
          ),
        }))
        .filter((group) => group.tags.length > 0);

      setFilteredTags(filteredTags.length > 0 ? filteredTags : allEventTags);
    },
    [allEventTags],
  );

  /**
   * When the combobox is opened, show all tags
   */
  const handleComboboxOpenChange = useCallback(() => {
    setFilteredTags([...allEventTags]);
  }, [allEventTags]);

  /**
   * Load the events based on the filters and look ahead months
   * - If look ahead months is 0, load all events (14 months)
   * - Apply filters in the query
   */
  const [
    loadEvents,
    { data: events, loading: loadingEvents, error: errorEvents },
  ] = useEventManyAppLazyQuery();
  useEffect(() => {
    loadEvents({
      variables: {
        startDate: format(new Date(), EVENT_DATE_FORMAT),
        endDate: format(
          addMonths(
            new Date(),
            widgetDetails.eventSettings.lookAheadMonths || 14,
          ),
          EVENT_DATE_FORMAT,
        ),
        organizationId: organizationId,
        status: EventStatus.Approved,
        tags: widgetDetails.eventSettings.eventTags,
      },
    });
  }, [organizationId, widgetDetails, loadEvents]);

  const handleClose = useCallback(() => {
    setWidgetDetails(defaultWidgetDetails);
    onClose();
  }, [onClose, setWidgetDetails, defaultWidgetDetails]);

  const handleNextClick = useCallback(() => {
    onNextClick(
      widgetDetails.type,
      widgetDetails.name,
      undefined,
      widgetDetails.eventSettings,
    );
    setWidgetDetails(defaultWidgetDetails);
  }, [
    onNextClick,
    setWidgetDetails,
    defaultWidgetDetails,
    widgetDetails.eventSettings,
    widgetDetails.name,
    widgetDetails.type,
  ]);

  /**
   * Set the handlers for the close, cancel, and next buttons
   */
  useEffect(() => {
    setOnCloseClick(() => handleClose);
    setCancelButtonProps((prev: ButtonProps) => ({
      ...prev,
      onClick: handleClose,
    }));
    setNextButtonProps((prev: ButtonProps) => ({
      ...prev,
      onClick: handleNextClick,
      disabled: widgetDetails.name.length < 1,
    }));
  }, [
    handleClose,
    handleNextClick,
    onClose,
    onNextClick,
    setOnCloseClick,
    setCancelButtonProps,
    setNextButtonProps,
    widgetDetails.name,
  ]);

  return {
    widgetDetails,
    lookAheadMonthsOptions,
    events: events?.eventMany,
    loadingEvents,
    errorEvents,
    allEventTags,
    filteredTags,
    loadingEventTags,
    errorEventTags,
    handleFilterTags,
    handleComboboxOpenChange,
    loading: false,
    error: null,
    setWidgetDetails,
  };
};
